import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import DashboardHome from './components/DashboardHome';
import Settings from './components/Settings';
import Roles from './components/Roles';
import AssignRoles from './components/AssignRoles';
import AdminUsers from './components/AdminUsers';
import Brokers from './components/Brokers';
import Parties from './components/Parties';
import ButtaCuttingParties from './components/ButtaCuttingParties';
import MillFinishParties from './components/MillFinishParties';
import BorderCuttingParties from './components/BorderCuttingParties';
import HandworkParties from './components/HandworkParties';
import Categories from './components/Categories';
import SubCategories from './components/SubCategories';
import Concepts from './components/Concepts';
import Feeders from './components/Feeders';
import ColorsMaster from './components/ColorsMaster';
import Designs from './components/Designs';
import Products from './components/Products';
import Sales from './components/Sales';
import Orders from './components/Orders';
import StockOrders from './components/StockOrders';
import Challans from './components/challans/Challans';
import Khatas from './components/Khatas';
import TransportParties from './components/TransportParties';
import ProtectedRoute from './components/ProtectedRoute';
import { PermissionProvider } from './permissions/PermissionContext';
import { LOGIN_SUCCESS_EVENT } from './authEvents';
import {
  isSessionExpired,
  msUntilSessionExpiry,
  touchLastActivity,
  getLastActivityMs,
  clearAuthStorage,
} from './utils/authSession';

const LOGOUT_EVENT_KEY = 'app:globalLogout';

function sessionStillValid() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    return !isSessionExpired(token);
  } catch {
    return false;
  }
}

function AppShell() {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(() => sessionStillValid());
  const [sessionChecked, setSessionChecked] = useState(false);
  const timeoutRef = useRef(null);

  const handleSessionExpired = (reason = 'expired') => {
    clearAuthStorage();

    try {
      localStorage.setItem(LOGOUT_EVENT_KEY, String(Date.now()));
    } catch {
      // ignore
    }

    if (reason === 'expired') {
      try {
        sessionStorage.setItem(
          'sessionExpiredMessage',
          'Your session has expired. Please sign in again.'
        );
      } catch {
        // ignore
      }
    }

    setIsAuthenticated(false);
  };

  const scheduleSessionTimeout = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    const remaining = msUntilSessionExpiry(token);

    if (remaining <= 0) {
      handleSessionExpired('expired');
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      handleSessionExpired('expired');
    }, remaining);
  };

  useEffect(() => {
    const checkExistingSession = () => {
      const token = localStorage.getItem('token');

      if (!token || isSessionExpired(token)) {
        if (token) {
          handleSessionExpired('expired');
        } else {
          setIsAuthenticated(false);
        }
        setSessionChecked(true);
        return;
      }

      if (getLastActivityMs() == null) {
        touchLastActivity();
      }

      setIsAuthenticated(true);
      setSessionChecked(true);
      scheduleSessionTimeout();
    };

    const handleStorage = (event) => {
      if (event.key === LOGOUT_EVENT_KEY) {
        handleSessionExpired('expired');
      }
    };

    const handleSessionInvalid = () => {
      handleSessionExpired('expired');
    };

    const handleLoginSuccess = () => {
      touchLastActivity();
      setIsAuthenticated(true);
      setSessionChecked(true);
      scheduleSessionTimeout();
    };

    const handleActivity = () => {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }
      touchLastActivity();
      scheduleSessionTimeout();
    };

    checkExistingSession();

    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('scroll', handleActivity);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('app:session-invalid', handleSessionInvalid);
    window.addEventListener(LOGIN_SUCCESS_EVENT, handleLoginSuccess);

    return () => {
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('app:session-invalid', handleSessionInvalid);
      window.removeEventListener(LOGIN_SUCCESS_EVENT, handleLoginSuccess);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!sessionChecked) {
    return null;
  }

  const guard = (menuId, element) => (
    <ProtectedRoute menuId={menuId}>{element}</ProtectedRoute>
  );

  const dashboardTree = (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Dashboard />
          ) : (
            <Navigate to="/login" replace state={{ from: location }} />
          )
        }
      >
        <Route
          index
          element={guard('Dashboard', <DashboardHome />)}
        />
        <Route
          path="sales"
          element={
            <ProtectedRoute ownerAdminOnly>
              <Sales />
            </ProtectedRoute>
          }
        />
        <Route path="orders/*" element={guard('Orders', <Orders />)} />
        <Route path="stock/*" element={guard('Stock', <StockOrders />)} />
        <Route path="challans/*" element={guard('Challans', <Challans />)} />
        <Route path="products" element={guard('Products', <Products />)} />
        <Route path="designs" element={guard('Designs', <Designs />)} />
        <Route path="masters/feeders" element={guard('Feeders', <Feeders />)} />
        <Route path="masters/colors" element={guard('Colors', <ColorsMaster />)} />
        <Route path="masters/production-units" element={guard('Production Units', <Khatas />)} />
        <Route path="insights/categories" element={guard('Categories', <Categories />)} />
        <Route path="insights/sub-categories" element={guard('Sub Categories', <SubCategories />)} />
        <Route path="insights/concepts" element={guard('Concepts', <Concepts />)} />
        <Route path="users/admin" element={guard('Admin User', <AdminUsers />)} />
        <Route path="users/brokers" element={guard('Brokers', <Brokers />)} />
        <Route path="users/parties" element={guard('Parties', <Parties />)} />
        <Route path="users/butta-cutting-parties" element={guard('Butta Cutting Parties', <ButtaCuttingParties />)} />
        <Route path="users/mill-finish-parties" element={guard('Mill Finish Parties', <MillFinishParties />)} />
        <Route path="users/border-cutting-parties" element={guard('Border Cutting Parties', <BorderCuttingParties />)} />
        <Route path="users/handwork-parties" element={guard('Handwork Parties', <HandworkParties />)} />
        <Route path="users/transport-parties" element={guard('Transport parties', <TransportParties />)} />
        <Route path="security/roles" element={guard('Roles', <Roles />)} />
        <Route path="security/assign-roles" element={guard('Assign Roles', <AssignRoles />)} />
        <Route path="settings" element={guard('Settings', <Settings />)} />
      </Route>
      <Route
        path="*"
        element={<Navigate to={isAuthenticated ? '/' : '/login'} replace />}
      />
    </Routes>
  );

  return isAuthenticated ? (
    <PermissionProvider>{dashboardTree}</PermissionProvider>
  ) : (
    dashboardTree
  );
}

export default function App() {
  return <AppShell />;
}
